import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Dropdown, Spin } from "antd";
import { BellOutlined, CloseOutlined } from "@ant-design/icons";
import {
  fetchLandlordNotifications,
  markAllLandlordNotificationsRead,
  markLandlordNotificationsRead,
} from "../../lib/api/notificationsApi";
import { useMobileWidgetContext } from "../../context/MobileWidgetContext";
import { trackProduct } from "../../lib/analytics/trackProduct";
import { device } from "../../lib/breakpoints";

const PAGE_SIZE = 15;
const SCROLL_LOAD_THRESHOLD_PX = 80;

const DATE_ONLY_TYPES = new Set(["rent_due_soon", "rent_overdue"]);

function formatWhen(iso, type) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";

    const dateOnly =
      DATE_ONLY_TYPES.has(type) ||
      (d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0) ||
      // Common Firestore/date-only UTC midnights that land as a round hour locally
      (d.getUTCHours() === 0 &&
        d.getUTCMinutes() === 0 &&
        d.getUTCSeconds() === 0);

    if (dateOnly) {
      return d.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      });
    }

    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function severityDot(severity) {
  if (severity === "urgent") return "#b91c1c";
  if (severity === "warning") return "#d97706";
  return "#2c5f2d";
}

/**
 * @param {{ variant: "desktop" | "mobile" }} props
 */
export default function NotificationBell({ variant = "desktop" }) {
  const navigate = useNavigate();
  const { setActiveWidget, clearActiveWidget } = useMobileWidgetContext();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [marking, setMarking] = useState(false);
  const bodyRef = useRef(null);
  const sentinelRef = useRef(null);
  const loadingMoreRef = useRef(false);
  const hasMoreRef = useRef(false);
  const nextCursorRef = useRef(null);

  const loadFirstPage = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchLandlordNotifications({
        limit: PAGE_SIZE,
        cursor: null,
      });
      setItems(Array.isArray(data.items) ? data.items : []);
      setUnreadCount(Number(data.unreadCount) || 0);
      setNextCursor(data.nextCursor ?? null);
      setHasMore(Boolean(data.hasMore));
    } catch (e) {
      console.error("[NOTIFICATIONS]", e);
      setItems([]);
      setUnreadCount(0);
      setNextCursor(null);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMore = useCallback(async () => {
    if (
      !hasMoreRef.current ||
      nextCursorRef.current == null ||
      loadingMoreRef.current
    ) {
      return;
    }
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const data = await fetchLandlordNotifications({
        limit: PAGE_SIZE,
        cursor: nextCursorRef.current,
      });
      const more = Array.isArray(data.items) ? data.items : [];
      setItems((prev) => {
        const seen = new Set(prev.map((p) => p.id));
        return [...prev, ...more.filter((m) => !seen.has(m.id))];
      });
      setUnreadCount(Number(data.unreadCount) || 0);
      setNextCursor(data.nextCursor ?? null);
      setHasMore(Boolean(data.hasMore));
    } catch (e) {
      console.error("[NOTIFICATIONS] load more", e);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    nextCursorRef.current = nextCursor;
  }, [nextCursor]);

  useEffect(() => {
    loadFirstPage();
    const id = setInterval(loadFirstPage, 60_000);
    return () => clearInterval(id);
  }, [loadFirstPage]);

  useEffect(() => {
    if (open) loadFirstPage();
  }, [open, loadFirstPage]);

  const setPanelOpen = useCallback(
    (nextOpen) => {
      setOpen((prev) => {
        const resolved =
          typeof nextOpen === "function" ? nextOpen(prev) : nextOpen;
        if (resolved && !prev) {
          trackProduct("notifications_opened", {
            variant,
            unread_count: unreadCount,
          });
        }
        return resolved;
      });
    },
    [variant, unreadCount]
  );

  useEffect(() => {
    if (variant !== "mobile") return undefined;
    if (open) {
      setActiveWidget("notifications");
      return () => clearActiveWidget();
    }
    clearActiveWidget();
    return undefined;
  }, [open, variant, setActiveWidget, clearActiveWidget]);

  // Auto-load next page when the user scrolls to the bottom (desktop + mobile)
  useEffect(() => {
    if (!open || !hasMore) return;
    const root = bodyRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadMore();
        }
      },
      {
        root,
        rootMargin: `${SCROLL_LOAD_THRESHOLD_PX}px`,
        threshold: 0,
      }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [open, hasMore, items.length, loadMore]);

  const handleBodyScroll = () => {
    const el = bodyRef.current;
    if (!el) return;
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (remaining <= SCROLL_LOAD_THRESHOLD_PX) {
      loadMore();
    }
  };

  const handleOpenItem = async (item) => {
    if (!item?.href) return;
    trackProduct("notification_clicked", {
      variant,
      notification_id: item.id,
      notification_type: item.type,
      severity: item.severity,
      was_unread: !item.read,
      href: item.href,
    });
    setPanelOpen(false);
    if (!item.read) {
      setItems((prev) =>
        prev.map((p) => (p.id === item.id ? { ...p, read: true } : p))
      );
      setUnreadCount((c) => Math.max(0, c - 1));
      markLandlordNotificationsRead([item.id]).catch(() => {});
    }
    navigate(item.href);
  };

  const handleMarkAllRead = async () => {
    setMarking(true);
    try {
      // Prefer server-side “all computable” so unloaded pages are included
      await markAllLandlordNotificationsRead();
      trackProduct("notifications_mark_all_read", {
        variant,
        unread_count: unreadCount,
      });
      setItems((prev) => prev.map((p) => ({ ...p, read: true })));
      setUnreadCount(0);
    } catch (e) {
      console.error("[NOTIFICATIONS] mark all", e);
    } finally {
      setMarking(false);
    }
  };

  const panel = (
    <Panel $mobile={variant === "mobile"}>
      <PanelHeader>
        <PanelTitleRow>
          <PanelTitle>Notifications</PanelTitle>
          {unreadCount > 0 ? (
            <HeaderCount aria-label={`${unreadCount} unread`}>
              {unreadCount > 99 ? "99+" : unreadCount}
            </HeaderCount>
          ) : null}
        </PanelTitleRow>
        <HeaderActions>
          {unreadCount > 0 && (
            <MarkAllButton
              type="button"
              disabled={marking}
              onClick={handleMarkAllRead}
            >
              Mark all read
            </MarkAllButton>
          )}
          {variant === "mobile" && (
            <CloseButton
              type="button"
              aria-label="Close notifications"
              onClick={() => setPanelOpen(false)}
            >
              <CloseOutlined />
            </CloseButton>
          )}
        </HeaderActions>
      </PanelHeader>

      <PanelBody ref={bodyRef} onScroll={handleBodyScroll}>
        {loading && items.length === 0 ? (
          <EmptyState>
            <Spin />
          </EmptyState>
        ) : items.length === 0 ? (
          <EmptyState>You&apos;re all caught up.</EmptyState>
        ) : (
          <>
            {items.map((item) => (
              <NotifRow
                key={item.id}
                type="button"
                $unread={!item.read}
                onClick={() => handleOpenItem(item)}
              >
                <SeverityDot style={{ background: severityDot(item.severity) }} />
                <NotifText>
                  <NotifTitle>{item.title}</NotifTitle>
                  <NotifBody>{item.body}</NotifBody>
                  <NotifWhen>{formatWhen(item.createdAt, item.type)}</NotifWhen>
                </NotifText>
              </NotifRow>
            ))}
            {hasMore ? <LoadSentinel ref={sentinelRef} aria-hidden /> : null}
            {loadingMore ? (
              <LoadMoreStatus>
                <Spin size="small" />
              </LoadMoreStatus>
            ) : null}
          </>
        )}
      </PanelBody>
    </Panel>
  );

  const trigger = (
    <BellButton
      type="button"
      aria-label={
        unreadCount > 0
          ? `Notifications, ${unreadCount} unread`
          : "Notifications"
      }
      onClick={() => {
        if (variant === "mobile") setPanelOpen((v) => !v);
      }}
    >
      <BellOutlined style={{ fontSize: 22 }} />
      {unreadCount > 0 ? (
        <CountBadge aria-hidden>
          {unreadCount > 99 ? "99+" : unreadCount}
        </CountBadge>
      ) : null}
    </BellButton>
  );

  if (variant === "mobile") {
    return (
      <>
        <BellTriggerWrap>{trigger}</BellTriggerWrap>
        {open && (
          <MobileOverlay
            role="dialog"
            aria-modal="true"
            aria-label="Notifications"
          >
            {panel}
          </MobileOverlay>
        )}
      </>
    );
  }

  return (
    <Dropdown
      trigger={["click"]}
      placement="bottomRight"
      open={open}
      onOpenChange={setPanelOpen}
      dropdownRender={() => panel}
      overlayStyle={{ padding: 0 }}
    >
      <BellTriggerWrap>{trigger}</BellTriggerWrap>
    </Dropdown>
  );
}

const BellTriggerWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  line-height: 0;
  vertical-align: middle;
`;

const BellButton = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin: 0;
  padding: 0;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: var(--color-text, #1f2937);
  cursor: pointer;
  line-height: 0;
  vertical-align: middle;
  transition: color 0.15s ease;

  &:hover {
    background: transparent;
    color: var(--color-primary, #2c5f2d);
  }

  .anticon {
    font-size: 22px !important;
    width: 22px;
    height: 22px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 0;
  }

  .anticon svg {
    width: 22px !important;
    height: 22px !important;
    display: block;
  }
`;

const CountBadge = styled.span`
  position: absolute;
  top: -3px;
  right: -5px;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 999px;
  background: #2c5f2d;
  color: #ffffff;
  font-size: 9px;
  font-weight: 700;
  line-height: 16px;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0;
  white-space: nowrap;
  overflow: visible;
  box-shadow: 0 0 0 1.5px #ffffff;
  pointer-events: none;
`;

const Panel = styled.div`
  width: ${(p) => (p.$mobile ? "100%" : "380px")};
  max-width: 100vw;
  height: ${(p) => (p.$mobile ? "100%" : "auto")};
  max-height: ${(p) => (p.$mobile ? "100%" : "min(70vh, 520px)")};
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: ${(p) => (p.$mobile ? "none" : "1px solid #e5e7eb")};
  border-radius: ${(p) => (p.$mobile ? "0" : "12px")};
  box-shadow: ${(p) =>
    p.$mobile ? "none" : "0 12px 40px rgba(15, 23, 42, 0.14)"};
  overflow: hidden;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.9rem 1rem;
  border-bottom: 1px solid #eef2f0;
  flex-shrink: 0;

  @media ${device.mobile}, ${device.tablet} {
    padding: 1.15rem 1.15rem 1.05rem;
    min-height: 3.5rem;
  }
`;

const PanelTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 700;
  color: #111827;

  @media ${device.mobile}, ${device.tablet} {
    font-size: 1.2rem;
    font-weight: 800;
    letter-spacing: -0.01em;
  }
`;

const PanelTitleRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  min-width: 0;

  @media ${device.mobile}, ${device.tablet} {
    gap: 0.55rem;
  }
`;

const HeaderCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.35rem;
  height: 1.35rem;
  padding: 0 0.4rem;
  border-radius: 999px;
  background: #2c5f2d;
  color: #ffffff;
  font-size: 0.7rem;
  font-weight: 700;
  line-height: 1;
  font-variant-numeric: tabular-nums;

  @media ${device.mobile}, ${device.tablet} {
    min-width: 1.55rem;
    height: 1.55rem;
    padding: 0 0.45rem;
    font-size: 0.8rem;
  }
`;

const HeaderActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
`;

const MarkAllButton = styled.button`
  border: none;
  background: transparent;
  color: var(--color-primary, #2c5f2d);
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0.25rem 0.35rem;

  @media ${device.mobile}, ${device.tablet} {
    font-size: 0.875rem;
    padding: 0.35rem 0.45rem;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: #f3f4f6;
  color: #111827;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  @media ${device.mobile}, ${device.tablet} {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    font-size: 1.05rem;
  }
`;

const PanelBody = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 0;
  -webkit-overflow-scrolling: touch;
`;

const LoadSentinel = styled.div`
  width: 100%;
  height: 1px;
  flex-shrink: 0;
`;

const LoadMoreStatus = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.85rem 1rem 1rem;
`;

const EmptyState = styled.div`
  padding: 2rem 1.25rem;
  text-align: center;
  color: #6b7280;
  font-size: 0.875rem;
`;

const NotifRow = styled.button`
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  text-align: left;
  border: none;
  border-bottom: 1px solid #f3f4f6;
  background: ${(p) => (p.$unread ? "#f4f8f4" : "#ffffff")};
  padding: 0.85rem 1rem;
  cursor: pointer;

  &:hover {
    background: #f8fafc;
  }
`;

const SeverityDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-top: 0.45rem;
  flex-shrink: 0;
`;

const NotifText = styled.span`
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
`;

const NotifTitle = styled.span`
  font-size: 0.8125rem;
  font-weight: 700;
  color: #111827;
  line-height: 1.3;
`;

const NotifBody = styled.span`
  font-size: 0.8125rem;
  color: #374151;
  line-height: 1.4;
`;

const NotifWhen = styled.span`
  font-size: 0.6875rem;
  color: #9ca3af;
  margin-top: 0.15rem;
`;

const MobileOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10000040;
  background: #ffffff;
`;
